import { useEffect, useMemo, useState } from 'react';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { useAuth } from '@/features/auth';
import { getStripe } from '@/features/events/utils/stripe-loader';
import { Modal } from '@/shared/components';
import { useToastStore } from '@/store';
import {
  createPlanRestoreSetupIntent,
  fetchPlanRestore,
  restorePlan,
  type PlanRestoreOption,
  type PlanRestoreResponse,
  type SavedCard,
} from '../services/settings-billing-service';
import './plan-restore-prompt.css';

type RestoreState = Extract<PlanRestoreResponse, { action_required: true }>;

const NEW_CARD = 'new';
// "Not Now" hides the prompt until the member signs in again; the stored value is the
// end of the session token, so a new sign-in shows it again.
const DISMISSED_KEY = 'wb.planRestoreDismissed';

function sessionMarker(): string {
  try {
    return (localStorage.getItem('wb.authToken') || '').slice(-12);
  } catch {
    return '';
  }
}

function isDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === sessionMarker();
  } catch {
    return false;
  }
}

function rememberDismissed() {
  try {
    sessionStorage.setItem(DISMISSED_KEY, sessionMarker());
  } catch {
    // Without storage the prompt simply shows again on the next page load.
  }
}

function roleLabel(role: string): string {
  return role
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function priceLabel(option: PlanRestoreOption): string {
  if (option.unit_amount == null) return '';
  return `$${(option.unit_amount / 100).toFixed(2)}/${option.interval || 'month'}`;
}

function cardLabel(card: SavedCard): string {
  const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card';
  const month = String(card.exp_month).padStart(2, '0');
  const year = String(card.exp_year).slice(-2);
  return `${brand} •••• ${card.last4}, ${card.is_expired ? 'expired' : 'expires'} ${month}/${year}`;
}

function cardElementOptions() {
  const isDark = document.documentElement.classList.contains('dark');
  return {
    style: {
      base: {
        color: isDark ? '#ffffff' : '#1e293b',
        iconColor: isDark ? '#cbd5e1' : '#475569',
        fontSize: '14px',
        '::placeholder': { color: '#94a3b8' },
      },
      invalid: { color: isDark ? '#f87171' : '#dc2626' },
    },
  };
}

/**
 * Shown after sign-in to a member who was moved to New Agent because a subscription
 * wasn't paid. They choose the plan they lost (or a lower one) and a card; it is charged
 * now and the plan's access comes back at once.
 */
export function PlanRestorePrompt() {
  const { user } = useAuth();
  const [state, setState] = useState<RestoreState | null>(null);
  const [open, setOpen] = useState(false);
  const stripePromise = useMemo(() => getStripe(), []);
  const userId = user?.id;
  const isAssistant = Boolean(user?.actingAs);

  useEffect(() => {
    if (!userId || isAssistant || isDismissed()) return;
    let cancelled = false;
    fetchPlanRestore()
      .then((response) => {
        if (cancelled || !response.action_required || response.options.length === 0) return;
        setState(response);
        setOpen(true);
      })
      .catch(() => {
        // The prompt is optional; a failed check leaves the app as it is.
      });
    return () => {
      cancelled = true;
    };
  }, [userId, isAssistant]);

  if (!state) return null;

  const close = () => {
    rememberDismissed();
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Restore Your Subscription"
      className="plan-restore-prompt"
      contentClassName="max-w-[560px]"
    >
      {stripePromise ? (
        <Elements stripe={stripePromise}>
          <RestoreForm
            state={state}
            onDone={() => {
              setOpen(false);
              setState(null);
            }}
            onNotNow={close}
          />
        </Elements>
      ) : (
        <p className="plan-restore-prompt__error">Payments aren't available right now. Please try again later.</p>
      )}
    </Modal>
  );
}

interface RestoreFormProps {
  state: RestoreState;
  onDone: () => void;
  onNotNow: () => void;
}

function RestoreForm({ state, onDone, onNotNow }: RestoreFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { refreshUser } = useAuth();
  const { addToast } = useToastStore();
  const usableCards = state.cards.filter((card) => !card.is_expired);
  const [priceId, setPriceId] = useState(
    (state.options.find((option) => option.is_previous) ?? state.options[0]).price_id
  );
  const [cardChoice, setCardChoice] = useState(usableCards[0]?.id ?? NEW_CARD);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const cardOptions = useMemo(() => cardElementOptions(), []);

  const selected = state.options.find((option) => option.price_id === priceId) ?? state.options[0];
  const previousPlan = roleLabel(state.request.previous_role);
  const currentPlan = roleLabel(state.request.current_role);

  const handleRestore = async () => {
    setError('');
    setSubmitting(true);
    try {
      let payment: { payment_method_id?: string; setup_intent_id?: string };
      if (cardChoice === NEW_CARD) {
        const cardElement = elements?.getElement(CardElement);
        if (!stripe || !cardElement) throw new Error('The card form is still loading. Please try again.');
        const setupIntent = await createPlanRestoreSetupIntent();
        const result = await stripe.confirmCardSetup(setupIntent.client_secret, {
          payment_method: { card: cardElement },
        });
        if (result.error) throw new Error(result.error.message || "This card couldn't be added.");
        payment = { setup_intent_id: result.setupIntent?.id || setupIntent.setup_intent_id };
      } else {
        payment = { payment_method_id: cardChoice };
      }
      const restored = await restorePlan({ price_id: selected.price_id, ...payment });
      await refreshUser();
      addToast({
        type: 'success',
        message: `Your ${roleLabel(restored.role || selected.role)} subscription is active again.`,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Your subscription couldn't be restored.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="plan-restore-prompt__body">
      <p className="plan-restore-prompt__lead">
        Your {previousPlan} subscription ended because the renewal payment wasn't received, and your
        account changed to {currentPlan}. Choose a plan to restore your access.
      </p>

      <fieldset className="plan-restore-prompt__group">
        <legend>Plan</legend>
        {state.options.map((option) => (
          <label key={option.price_id} className="plan-restore-prompt__choice">
            <input
              type="radio"
              name="restore-plan"
              value={option.price_id}
              checked={option.price_id === priceId}
              onChange={() => setPriceId(option.price_id)}
            />
            <span className="plan-restore-prompt__choice-name">
              {roleLabel(option.role)}
              {option.is_previous && <span className="plan-restore-prompt__tag">Previous plan</span>}
            </span>
            <span className="plan-restore-prompt__choice-meta">{priceLabel(option)}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className="plan-restore-prompt__group">
        <legend>Payment Method</legend>
        {state.cards.map((card) => (
          <label
            key={card.id}
            className={`plan-restore-prompt__choice${card.is_expired ? ' is-disabled' : ''}`}
          >
            <input
              type="radio"
              name="restore-card"
              value={card.id}
              disabled={card.is_expired}
              checked={cardChoice === card.id}
              onChange={() => setCardChoice(card.id)}
            />
            <span className="plan-restore-prompt__choice-name">{cardLabel(card)}</span>
          </label>
        ))}
        <label className="plan-restore-prompt__choice">
          <input
            type="radio"
            name="restore-card"
            value={NEW_CARD}
            checked={cardChoice === NEW_CARD}
            onChange={() => setCardChoice(NEW_CARD)}
          />
          <span className="plan-restore-prompt__choice-name">Add a new card</span>
        </label>
        {cardChoice === NEW_CARD && (
          <div className="plan-restore-prompt__card">
            <CardElement options={cardOptions} />
          </div>
        )}
      </fieldset>

      <p className="plan-restore-prompt__note">
        You'll be charged {priceLabel(selected) || 'the plan price'} today, and your subscription renews
        monthly from today.
      </p>

      {error && (
        <p className="plan-restore-prompt__error" role="alert">
          {error}
        </p>
      )}

      <div className="plan-restore-prompt__actions">
        <button type="button" className="plan-restore-prompt__secondary" onClick={onNotNow} disabled={submitting}>
          Not Now
        </button>
        <button type="button" className="plan-restore-prompt__primary" onClick={handleRestore} disabled={submitting}>
          {submitting ? 'Restoring…' : `Restore ${roleLabel(selected.role)}`}
        </button>
      </div>
    </div>
  );
}
